import { tap } from 'rxjs';
import { AfterViewInit, ChangeDetectionStrategy, Component, ViewChild, inject } from '@angular/core';
import {
  ControlEvent,
  FormControlStatus,
  NgForm,
  PristineChangeEvent,
  StatusChangeEvent,
  TouchedChangeEvent,
  ValueChangeEvent,
} from '@angular/forms';
import { FormStoreEventPayload, ReportStore } from '@navstation-portal/reporting/data-access/vessel-report';
import {
  AnyOffshoreSectionState,
  OffshoreFormStoreInitPayload,
} from '@navstation-portal/reporting/data-model/offshore-report';
import { FieldType, FormFieldConfig } from '@navstation-portal/reporting/data-model/ui';
import { StateFieldType } from '@navstation-portal/reporting/data-model/vessel-report';
import { Units } from '@navstation-portal/shared/data-model/ui';

interface BaseChangedEvent {
  sectionId: string;
  fieldId: string;
}

export interface InitialiseFormStateEvent extends BaseChangedEvent {
  initialState: AnyOffshoreSectionState;
}

export interface TouchedChangedEvent extends BaseChangedEvent {
  value: boolean;
}

export interface PristineChangedEvent extends BaseChangedEvent {
  value: boolean;
}

export interface StatusChangedEvent extends BaseChangedEvent {
  value: FormControlStatus;
}

export interface ValueChangedEvent extends BaseChangedEvent {
  value: StateFieldType;
}

@Component({ standalone: true, template: '', changeDetection: ChangeDetectionStrategy.OnPush })
export abstract class AbstractStoreConnectedSectionComponent implements AfterViewInit {
  // subclasses that extend this component must add #form="ngForm"
  // to their <form> or nothing will work
  @ViewChild('form', { read: NgForm }) form!: NgForm;

  readonly reportStore = inject(ReportStore);

  // required to use `fieldType.Number`, `fieldType.Text`, etc in component html
  public get fieldType(): typeof FieldType {
    return FieldType;
  }

  // tabId is set for finding where to look up form field rules, we need to
  // know what tab we're on, e.g. operational, power, bunker, etc
  abstract tabId: string;

  // sectionId must be set in the subclass as the name to be used as the key
  // that will go into the store for this section of data (e.g. vesselDetails)
  abstract sectionId: string;

  units = Units;

  abstract events: {
    initialiseFormState: (payload: OffshoreFormStoreInitPayload) => void;
    updateFormStateTouched: (payload: FormStoreEventPayload) => void;
    updateFormStatePristine: (payload: FormStoreEventPayload) => void;
    updateFormStateStatus: (payload: FormStoreEventPayload) => void;
    updateFormStateValue: (payload: FormStoreEventPayload) => void;
  };

  getFormRulesForField(fieldId: string): FormFieldConfig | undefined {
    const rules = this.reportStore.formFieldRules();

    if (!rules) return;

    const tabRules = rules[this.tabId];
    if (!tabRules) return;

    const sectionRules = tabRules[this.sectionId];
    if (!sectionRules) return;

    const fieldRules = sectionRules.find((field) => field.fieldId === fieldId);
    if (!fieldRules) return;

    return fieldRules;
  }

  isFieldRequired(fieldId: string): boolean {
    const fieldRules = this.getFormRulesForField(fieldId);
    return fieldRules?.isRequired ?? false;
  }

  isFieldReadOnly(fieldId: string): boolean {
    const fieldRules = this.getFormRulesForField(fieldId);
    return fieldRules?.isReadOnly ?? false;
  }

  ngAfterViewInit() {
    this.initialise();
  }

  initialise() {
    if (!this.form) return;
    const subscribedKeys: string[] = [];
    this.form.statusChanges
      ?.pipe(
        tap(() => {
          const controlKeys = Object.keys(this.form.controls);
          const removedKeys = subscribedKeys.filter((key) => !controlKeys.includes(key));
          for (let i = subscribedKeys.length - 1; i >= 0; i--) {
            if (removedKeys.includes(subscribedKeys[i])) {
              subscribedKeys.splice(i, 1);
            }
          }

          controlKeys.forEach((key) => {
            const control = this.form.controls[key];
            if (subscribedKeys.includes(key)) return;

            control?.events.subscribe((e) => this.handleEvent(e, key));
            control?.updateValueAndValidity({
              onlySelf: true,
            });

            subscribedKeys.push(key);
          });
        })
      )
      .subscribe();
  }

  handleEvent(event: ControlEvent<unknown>, key: string) {
    switch (event.constructor) {
      case TouchedChangeEvent:
        return this.handleTouchedChangeEvent(event as TouchedChangeEvent, key);
      case PristineChangeEvent:
        return this.handlePristineChangeEvent(event as PristineChangeEvent, key);
      case StatusChangeEvent:
        return this.handleStatusChangeEvent(event as StatusChangeEvent, key);
      case ValueChangeEvent:
        return this.handleValueChangeEvent(event as ValueChangeEvent<StateFieldType>, key);
      default:
        console.error('Unknown ControlEvent type:', event.constructor);
        return;
    }
  }

  handleTouchedChangeEvent(event: TouchedChangeEvent, key: string) {
    const [fieldId, property, index] = key.split('-');

    if (index == undefined) {
      return this.dispatchTouchedChanged({
        sectionId: this.sectionId,
        fieldId: key,
        value: event.touched,
      });
    }

    if (this.dispatchTouchedChangedArray) {
      return this.dispatchTouchedChangedArray(
        {
          sectionId: this.sectionId,
          fieldId,
          value: event.touched,
        },
        parseInt(index),
        property
      );
    }
  }

  handlePristineChangeEvent(event: PristineChangeEvent, key: string) {
    const [fieldId, property, index] = key.split('-');

    if (index == undefined) {
      return this.dispatchPristineChanged({
        sectionId: this.sectionId,
        fieldId: key,
        value: event.pristine,
      });
    }

    if (this.dispatchPristineChangedArray) {
      return this.dispatchPristineChangedArray(
        {
          sectionId: this.sectionId,
          fieldId,
          value: event.pristine,
        },
        parseInt(index),
        property
      );
    }
  }

  handleStatusChangeEvent(event: StatusChangeEvent, key: string) {
    const [fieldId, property, index] = key.split('-');

    if (index == undefined) {
      return this.dispatchStatusChanged({
        sectionId: this.sectionId,
        fieldId: key,
        value: event.status,
      });
    }

    if (this.dispatchStatusChangedArray) {
      return this.dispatchStatusChangedArray(
        {
          sectionId: this.sectionId,
          fieldId,
          value: event.status,
        },
        parseInt(index),
        property
      );
    }
  }

  handleValueChangeEvent(event: ValueChangeEvent<StateFieldType>, key: string) {
    const [fieldId, property, index] = key.split('-');

    if (index == undefined) {
      return this.dispatchValueChanged({
        sectionId: this.sectionId,
        fieldId: key,
        value: event.value,
      });
    }

    if (this.dispatchValueChangedArray) {
      return this.dispatchValueChangedArray(
        {
          sectionId: this.sectionId,
          fieldId,
          value: event.value,
        },
        parseInt(index),
        property
      );
    }
  }

  dispatchTouchedChanged(state: TouchedChangedEvent) {
    this.events.updateFormStateTouched(state);
  }

  dispatchTouchedChangedArray(state: TouchedChangedEvent, index: number, property: string) {
    this.events.updateFormStateTouched({
      ...state,
      index,
      property,
    });
  }

  dispatchPristineChanged(state: PristineChangedEvent) {
    this.events.updateFormStatePristine(state);
  }

  dispatchPristineChangedArray(state: PristineChangedEvent, index: number, property: string) {
    this.events.updateFormStatePristine({
      ...state,
      index,
      property,
    });
  }

  dispatchStatusChanged(state: StatusChangedEvent) {
    this.events.updateFormStateStatus(state);
  }

  dispatchStatusChangedArray(state: StatusChangedEvent, index: number, property: string) {
    this.events.updateFormStateStatus({
      ...state,
      index,
      property,
    });
  }

  dispatchValueChanged(state: ValueChangedEvent) {
    this.events.updateFormStateValue(state);
  }

  dispatchValueChangedArray(state: ValueChangedEvent, index: number, property: string) {
    this.events.updateFormStateValue({
      ...state,
      index,
      property,
    });
  }
}
